import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getDashboardStats, getLatestPrices, getAlerts, seedDemoData, downloadMarketReport } from '../services/api';
import { sortContracts, deduplicateContracts } from '../utils/contractUtils';
import { Skeleton } from '../components/ui/skeleton';
import { Button } from '../components/ui/button';
import { Bell, Zap, Flame, Phone, FileDown, TrendingUp, TrendingDown } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import AlertCard from '../components/AlertCard';

const CalPriceRow = ({ label, value, change, icon: Icon, iconColor }) => (
  <div className="flex items-center justify-between py-3 border-b border-[#BFC6CC]/10 last:border-b-0">
    <div className="flex items-center gap-3">
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${iconColor}`}>
        <Icon className="w-4 h-4" />
      </div>
      <span className="text-sm font-medium text-[#191D1E]">{label}</span>
    </div>
    <div className="flex items-center gap-3">
      <span className="font-mono font-bold text-[#191D1E]">{value} €/MWh</span>
      {change !== undefined && change !== null && (
        <span className={`flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${
          change >= 0 ? 'bg-red-50 text-red-500' : 'bg-[#10D183]/10 text-[#10D183]'
        }`}>
          {change >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
          {change >= 0 ? '+' : ''}{change?.toFixed(2)}%
        </span>
      )}
    </div>
  </div>
);

const ClientDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [prices, setPrices] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [statsRes, pricesRes, alertsRes] = await Promise.all([
        getDashboardStats(),
        getLatestPrices(),
        getAlerts(),
      ]);
      setStats(statsRes.data);
      setPrices(pricesRes.data);
      setAlerts(alertsRes.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSeedData = async () => {
    try {
      await seedDemoData();
      toast.success('Données de démonstration chargées !');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    }
  };

  const handleDownloadReport = async () => {
    try {
      toast.info('Génération du rapport...');
      const response = await downloadMarketReport();
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `rapport_marche_${new Date().toISOString().slice(0,10)}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Rapport téléchargé !');
    } catch (error) {
      toast.error('Erreur lors de la génération du rapport');
    }
  };

  // Filter only CAL contracts (Cal27, Cal28, Cal29, Cal30)
  const allPrices = deduplicateContracts(prices);
  const calPattern = /cal\s*2[7890]/i;
  const calElec = sortContracts(allPrices.filter(p => p.energy_type === 'electricity' && calPattern.test(p.contract_name)));
  const calGas = sortContracts(allPrices.filter(p => p.energy_type === 'gas' && calPattern.test(p.contract_name)));

  const triggeredAlerts = alerts.filter(a => a.triggered_at !== null);
  const activeAlerts = alerts.filter(a => a.is_active);

  if (loading) {
    return (
      <div className="space-y-5" data-testid="client-dashboard-loading">
        <Skeleton className="h-20 bg-white" />
        <Skeleton className="h-48 bg-white" />
        <Skeleton className="h-64 bg-white" />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="client-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#191D1E]">
            Bonjour !
          </h1>
          <p className="text-[#687175] mt-1">Votre espace Place des Energies</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleDownloadReport} className="btn-secondary" data-testid="download-market-report-btn">
            <FileDown className="w-4 h-4 mr-2" /> Mon rapport
          </Button>
          <Link to="/dashboard/callback">
            <Button className="btn-primary" data-testid="request-callback-btn">
              <Phone className="w-4 h-4 mr-2" /> Demander un rappel
            </Button>
          </Link>
        </div>
      </div>

      {/* Triggered Alerts Banner */}
      {triggeredAlerts.length > 0 && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white border-2 border-[#10D183] rounded-2xl p-5"
          data-testid="triggered-alerts-banner"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-[#10D183]/10 flex items-center justify-center">
              <Bell className="w-6 h-6 text-[#10D183]" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-[#191D1E]">
                {triggeredAlerts.length} alerte(s) déclenchée(s) !
              </h3>
              <p className="text-sm text-[#687175]">
                Le moment est peut-être idéal pour renégocier votre contrat
              </p>
            </div>
            <Link to="/dashboard/callback">
              <Button className="btn-primary" data-testid="callback-from-alert-btn">
                Demander un rappel
              </Button>
            </Link>
          </div>
        </motion.div>
      )}

      {/* My Alerts — Section principale en haut */}
      <div className="bg-white border border-[#BFC6CC]/12 rounded-2xl p-5" data-testid="my-alerts-section">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-[#191D1E] flex items-center gap-2">
            <Bell className="w-5 h-5 text-[#397DFF]" /> Mes alertes
          </h2>
          <span className="text-xs font-medium text-[#687175] bg-[#F5F6F9] px-3 py-1 rounded-full">
            {activeAlerts.length} active(s)
          </span>
        </div>
        {alerts.length === 0 ? (
          <p className="text-sm text-[#687175] py-4 text-center">
            Aucune alerte configurée. Votre conseiller peut en créer pour vous.
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {alerts.map(alert => (
              <AlertCard key={alert.id} alert={alert} showActions={false} />
            ))}
          </div>
        )}
      </div>

      {/* CAL Prices — Simplified */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Electricity CAL */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white border border-[#BFC6CC]/12 rounded-2xl p-5"
          data-testid="cal-elec-section"
        >
          <h3 className="text-base font-bold text-[#191D1E] flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-[#F9C80E]/10 flex items-center justify-center">
              <Zap className="w-4 h-4 text-[#F9C80E]" />
            </div>
            Electricité — Prix CAL
          </h3>
          {calElec.length === 0 ? (
            <p className="text-sm text-[#687175] py-2">Aucun prix CAL disponible</p>
          ) : (
            <div>
              {calElec.map(price => (
                <CalPriceRow
                  key={price.contract_name}
                  label={price.contract_name}
                  value={price.price?.toFixed(2)}
                  change={price.variation_pct}
                  icon={Zap}
                  iconColor="bg-[#F9C80E]/10 text-[#F9C80E]"
                />
              ))}
            </div>
          )}
        </motion.div>

        {/* Gas CAL */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white border border-[#BFC6CC]/12 rounded-2xl p-5"
          data-testid="cal-gas-section"
        >
          <h3 className="text-base font-bold text-[#191D1E] flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-[#397DFF]/10 flex items-center justify-center">
              <Flame className="w-4 h-4 text-[#397DFF]" />
            </div>
            Gaz Naturel — Prix CAL
          </h3>
          {calGas.length === 0 ? (
            <p className="text-sm text-[#687175] py-2">Aucun prix CAL disponible</p>
          ) : (
            <div>
              {calGas.map(price => (
                <CalPriceRow
                  key={price.contract_name}
                  label={price.contract_name}
                  value={price.price?.toFixed(2)}
                  change={price.variation_pct}
                  icon={Flame}
                  iconColor="bg-[#397DFF]/10 text-[#397DFF]"
                />
              ))}
            </div>
          )}
        </motion.div>
      </div>

      {prices.length === 0 && (
        <div className="text-center py-6">
          <p className="text-[#687175] mb-4">Aucun prix disponible</p>
          <Button onClick={handleSeedData} className="btn-secondary" data-testid="client-seed-btn">
            Charger données démo
          </Button>
        </div>
      )}
    </div>
  );
};

export default ClientDashboard;
